<script setup lang="ts">
import type { UnscheduledItem } from '../types'
import imgPendingWhite from '@/assets/pending_white@2x.png'
import imgPendingBlack from '@/assets/pending_black@2x.png'

defineProps<{
  heading: string
  items: UnscheduledItem[]
  selectedOrderId: string | null
  embedded: boolean
}>()

const emit = defineEmits<{
  select: [item: UnscheduledItem]
}>()
</script>

<template>
  <aside
    class="schedule-panel schedule-panel--list"
    :class="{ 'schedule-ctx--embedded': embedded }"
  >
    <h2 class="panel-heading">{{ heading }}</h2>
    <ul class="unscheduled-list">
      <li
        v-for="item in items"
        :key="item.orderId"
        class="unscheduled-card"
        :class="{ 'unscheduled-card--active': item.orderId === selectedOrderId }"
      >
        <button type="button" class="unscheduled-card-btn" @click="emit('select', item)">
          <span class="unscheduled-hourglass" aria-hidden="true">
            <img
              :src="item.orderId === selectedOrderId ? imgPendingBlack : imgPendingWhite"
              class="unscheduled-pending-ic"
              alt=""
            />
          </span>
          <span class="unscheduled-text">
            <span class="unscheduled-addr">{{ item.address }}</span>
            <span class="unscheduled-id">{{ item.orderId }}</span>
          </span>
        </button>
      </li>
    </ul>
  </aside>
</template>

<style scoped>
.schedule-panel--list.schedule-ctx--embedded {
  position: relative;
  align-self: start;
  width: 205px;
  min-width: 205px;
  max-width: 205px;
  height: 638px;
  min-height: 638px;
  max-height: 638px;
  background: #484848;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
  border-right: 1px solid #ffffff;
}

.schedule-panel--list.schedule-ctx--embedded > .panel-heading {
  flex-shrink: 0;
  box-sizing: border-box;
  width: 171px;
  height: 20px;
  margin: 16px 17px 16px;
  padding: 0;
  background: #484848;
  font-family: SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: 0;
  color: #ffffff;
  text-align: left;
  white-space: nowrap;
}

.schedule-panel--list.schedule-ctx--embedded > .unscheduled-list {
  flex: 1;
  min-height: 0;
  margin: 0;
  padding: 0 17px 17px;
  overflow-x: hidden;
  overflow-y: auto;
  box-sizing: border-box;
  align-items: flex-start;
  gap: 8px;
}

.schedule-ctx--embedded .unscheduled-card {
  width: 171px;
  max-width: 171px;
}

.schedule-ctx--embedded .unscheduled-card-btn {
  width: 171px;
  max-width: 171px;
  height: 42px;
  min-height: 42px;
  max-height: 42px;
  padding: 6px 8px;
  gap: 8px;
  border: none;
  border-radius: 8px;
  background: transparent;
  align-items: center;
  box-sizing: border-box;
  overflow: hidden;
}

.schedule-ctx--embedded .unscheduled-card--active .unscheduled-card-btn {
  background: #ffffff;
  border-radius: 8px;
  box-shadow: none;
  color: #111827;
}

.schedule-ctx--embedded .unscheduled-text {
  gap: 2px;
  min-width: 0;
}

.schedule-ctx--embedded .unscheduled-addr {
  display: block;
  box-sizing: border-box;
  width: 105px;
  max-width: 105px;
  height: 16px;
  margin: 0;
  padding: 0;
  font-family: SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 16px;
  color: #ffffff;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.schedule-ctx--embedded .unscheduled-card--active .unscheduled-addr {
  color: #000000;
}

.schedule-ctx--embedded .unscheduled-id {
  display: block;
  box-sizing: border-box;
  width: 121px;
  max-width: 121px;
  height: 16px;
  margin: 0;
  padding: 0;
  font-family: SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  color: #484848;
  text-align: left;
  opacity: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: normal;
}

.schedule-ctx--embedded .unscheduled-card:not(.unscheduled-card--active) .unscheduled-id {
  color: rgba(255, 255, 255, 0.75);
}

.schedule-ctx--embedded .unscheduled-hourglass {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  margin: 0;
}

.schedule-ctx--embedded .unscheduled-pending-ic {
  width: 16px;
  height: 16px;
  display: block;
  object-fit: contain;
}

.schedule-panel--list {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #3d3d3d;
  color: #ffffff;
  padding: 0;
  border-right: 1px solid #ffffff;
}

.schedule-panel--list > .panel-heading {
  flex-shrink: 0;
  margin: 0;
  padding: 20px 16px 16px;
  background: #3d3d3d;
}

.schedule-panel--list > .unscheduled-list {
  flex: 1;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 0 16px 20px;
}

.panel-heading {
  margin: 0 0 16px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: rgba(255, 255, 255, 0.85);
}

.unscheduled-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.unscheduled-card-btn {
  width: 100%;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 14px;
  border: none;
  border-radius: 12px;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
  box-sizing: border-box;
  box-shadow: 0 0 0 transparent;
}

.unscheduled-card--active .unscheduled-card-btn {
  background: #ffffff;
  color: #111827;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}

.unscheduled-hourglass {
  flex-shrink: 0;
  margin-top: 2px;
}

.unscheduled-pending-ic {
  width: 18px;
  height: 22px;
  display: block;
  object-fit: contain;
}

.unscheduled-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.unscheduled-addr {
  font-size: 15px;
  font-weight: 600;
  line-height: 1.25;
}

.unscheduled-id {
  font-size: 12px;
  opacity: 0.75;
  word-break: break-all;
}

.unscheduled-card--active .unscheduled-id {
  color: #6b7280;
  opacity: 1;
}
</style>
